import { CSSProperties } from 'styled-components';

import { EnergyCostGraphNorlys } from '~src/components/graph/EnergyCostGraph';
import { NumberInputNorlys } from '~src/components/NumberInput';
import { SelectNorlys } from '~src/components/Select';
import { SliderNorlys } from '~src/components/Slider';
import { PRIMARY_HEATING_OPTIONS } from '~src/constants/heating';
import { useDisplayContext } from '~src/context/DisplayContext';
import { useEnergyOptions } from '~src/context/EnergyOptionsContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { DeepPartial, Residence } from '~src/model';
import { findConstantForHeating } from '~src/util';

import { useAnnualConsumptionPrices, useConsumptionSliderProps } from './util';

export const ConsumptionInformationNorlys = () => {
  const { isMobile } = useDisplayContext();

  return isMobile ? <Mobile /> : <Desktop />;
};

const Mobile = () => {
  const { isMobile } = useDisplayContext();

  const { residence, setResidence, offerProducts } = usePublicCalculatorContext();

  const updateResidence = (input: DeepPartial<Residence>) => setResidence?.({ ...residence, ...input });

  const prices = useAnnualConsumptionPrices(offerProducts, residence);

  const sliderProps = useConsumptionSliderProps(residence, updateResidence);

  return (
    <div {...{ style: isMobile ? { paddingTop: '16px' } : {} }}>
      <SelectNorlys
        {...{
          style: { margin: '16px 0' },
          label: 'Primær varmekilde',
          options: PRIMARY_HEATING_OPTIONS,
          value: residence?.primaryHeating?.fuelType,
          placeholder: 'Varmekilde',
          onChange: (fuelType?: string) => updateResidence({ primaryHeating: { fuelType } }),
        }}
      />

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {sliderProps.map(properties => (
          <SliderNorlys key={`${properties.label}`} {...properties} />
        ))}
        <EnergyCostGraphNorlys
          {...{
            ...prices,
            title: 'Forventet årlig energiforbrug',
            style: { width: '100%' },
            maxDecimals: 0,
          }}
        />
      </div>
    </div>
  );
};

const Desktop = () => {
  const { isMobile } = useDisplayContext();

  const { residence, setResidence, offerProducts } = usePublicCalculatorContext();
  const { primaryHeating, energyExpenditure } = residence ?? {};

  const updateResidence = (input: DeepPartial<Residence>) => setResidence?.({ ...residence, ...input });

  const energyOptions = useEnergyOptions();

  const constant = findConstantForHeating(primaryHeating, energyOptions);

  const prices = useAnnualConsumptionPrices(offerProducts, residence);

  const inputStyle: CSSProperties = {
    width: '50%',
    minWidth: '360px',
    margin: '16px auto',
  };

  return (
    <div {...{ style: isMobile ? { paddingTop: '16px' } : {} }}>
      <EnergyCostGraphNorlys
        {...{
          ...prices,
          title: 'Forventet årlig energiforbrug',
          style: { margin: '0 40px 32px 40px' },
          maxDecimals: 0,
        }}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexFlow: 'column wrap',
            justifyContent: 'space-around',
            padding: '32px 16px 48px 16px',
          }}
        >
          <SelectNorlys
            {...{
              size: 'large',
              style: inputStyle,
              label: 'Primær varmekilde',
              options: PRIMARY_HEATING_OPTIONS,
              value: residence?.primaryHeating?.fuelType,
              placeholder: 'Varmekilde',
              onChange: (fuelType?: string) => updateResidence({ primaryHeating: { fuelType } }),
            }}
          />

          <NumberInputNorlys
            {...{
              size: 'large',
              style: inputStyle,
              value: energyExpenditure,
              label: 'Årligt elforbrug',
              addonAfter: 'kWh',
              onChange: value => updateResidence?.({ energyExpenditure: value }),
            }}
          />

          <NumberInputNorlys
            {...{
              size: 'large',
              style: inputStyle,
              addonAfter: constant?.denominator ?? 'm3',
              value: primaryHeating?.annualUsage,
              label: 'Årligt varmeforbrug',
              onChange: (annualUsage?: number) =>
                updateResidence?.({ primaryHeating: { ...residence?.primaryHeating, annualUsage } }),
            }}
          />
        </div>
      </div>
    </div>
  );
};
